import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useRef, useState } from 'react';

import { company } from '~/company/Company';
import { useGlobal } from '~/hooks/useGlobal';
import { useOnWarehouseChange } from '~/hooks/useOnWarehouseChange';
import { Category, Product } from '~/stores/CategoriesStore';

import ProductCard from '../../ProductCard/ProductCard';

import CategoryBlockTitle from '../CategoryBlockTitle';
import { createCategoryUrl } from '../utils';

import GridFeedbackForm from './components/GridFeedbackForm/GridFeedbackForm';
import styles from './ProductsGridBlock.module.scss';

interface ProductsGridBlockProps {
  category: Category;
}

const ProductsGridBlock = ({ category }: ProductsGridBlockProps) => {
  useGlobal();
  useOnWarehouseChange(() => {
    category.productsRequest.run();
  });
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [columnsCount, setColumnsCount] = useState(1);

  useEffect(() => {
    const gridElement = gridRef.current;
    if (!gridElement) {
      return;
    }

    const updateColumns = () => {
      const columns = getComputedStyle(gridElement).gridTemplateColumns;
      const count = columns.split(' ').filter(Boolean).length;
      setColumnsCount(Math.max(count, 1));
    };

    updateColumns();

    const resizeObserver = new ResizeObserver(updateColumns);
    resizeObserver.observe(gridElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const products = category?.products ?? [];
  const gridItems = useMemo(() => {
    const items = products.map((product: Product) => (
      <ProductCard
        product={product}
        className={styles.product}
        key={product?.id}
        variant={company.productCardVariantXL}
      />
    ));

    if (!company.insertGridFeedbackInProductsGrid || !items.length) {
      return items;
    }

    const insertIndex = Math.min(Math.max(columnsCount, 1) * 2, items.length);

    const itemsWithForm = [...items];
    itemsWithForm.splice(
      insertIndex,
      0,
      <GridFeedbackForm key="grid-feedback" />,
    );
    return itemsWithForm;
  }, [products, columnsCount]);

  if (!products.length && !category.productsRequest.isLoading) {
    return null;
  }

  return (
    <div data-company={company.name} className={styles.container}>
      <CategoryBlockTitle
        text={category?.name ?? 'n/a'}
        categoryId={category?.subcategory?.[0]?.id}
        // @ts-ignore FIXME: migrate to noUncheckedIndexedAccess: true
        linkTo={
          category.subcategory?.[0]?.id
            ? createCategoryUrl({ id: category.subcategory?.[0]?.id })
            : '/shop'
        }
      />
      <div data-company={company.name} className={styles.grid} ref={gridRef}>
        {gridItems}
      </div>
    </div>
  );
};

export default observer(ProductsGridBlock);
